import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { customerLogos } from '../../data/customerLogos';

function fmtUsdShort(amount: number): string {
  if (!Number.isFinite(amount)) return '-';
  const abs = Math.abs(amount);
  const unit = abs >= 1_000_000 ? 'M' : 'K';
  const div = abs >= 1_000_000 ? 1_000_000 : 1_000;
  const v = amount / div;
  const decimals = Number.isInteger(v) ? 0 : abs >= 1_000_000 ? (v >= 10 ? 0 : 1) : v >= 10 ? 1 : 0;
  const shown = v.toFixed(decimals);
  return `$${shown}${unit}`;
}

function Donut({ percent, color }: { percent: number; color: string }) {
  const p = Math.max(0, Math.min(100, percent));
  const r = 16;
  const c = 2 * Math.PI * r;
  const dash = (p / 100) * c;
  return (
    <svg className="donut" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
      <circle className="donut__bg" cx="20" cy="20" r={r} />
      <circle
        className="donut__fg"
        cx="20"
        cy="20"
        r={r}
        style={{ stroke: color, strokeDasharray: `${dash} ${c}` }}
        transform="rotate(-90 20 20)"
      />
    </svg>
  );
}

function SemesterBarChart({
  title,
  subtitle,
  metricLabel,
  items,
  tone,
}: {
  title: string;
  subtitle: string;
  metricLabel: string;
  items: Array<{ period: string; amount: number }>;
  tone: 'revenue' | 'contracts';
}) {
  const max = Math.max(1, ...items.map((item) => item.amount));

  return (
    <div className="card traction-trend">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <div style={{ fontWeight: 900 }}>{title}</div>
        <div className="pill">USD</div>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
        {subtitle}
      </div>
      <div className="growth-chart" role="figure" aria-label={`${title} by semester`}>
        <div className="growth-chart__legend">
          <span>
            <span className={`growth-chart__dot growth-chart__dot--${tone}`} aria-hidden="true" /> {metricLabel}
          </span>
        </div>
        <div className="growth-chart__plot">
          {items.map((item) => (
            <div key={item.period} className="growth-chart__column">
              <div className="growth-chart__value">{fmtUsdShort(item.amount)}</div>
              <div className="growth-chart__bar-slot" aria-label={`${metricLabel} ${fmtUsdShort(item.amount)}`}>
                <div
                  className={`growth-chart__bar growth-chart__bar--${tone}`}
                  style={{ height: `${Math.max(8, (item.amount / max) * 100)}%` }}
                />
              </div>
              <div className="growth-chart__label">{item.period}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

type Logo = { name: string; src: string };

function LogoCell({ logo }: { logo: Logo }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return <div className="logo-cell__fallback">{logo.name}</div>;
  }
  return <img src={logo.src} alt={logo.name} loading="lazy" onError={() => setBroken(true)} />;
}

function LogoCarousel({ logos }: { logos: Logo[] }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chunkRef = useRef<HTMLDivElement | null>(null);
  const [isCarousel, setIsCarousel] = useState(false);
  const [distance, setDistance] = useState(0);
  const [duration, setDuration] = useState(28);

  useEffect(() => {
    const container = containerRef.current;
    const chunk = chunkRef.current;
    if (!container || !chunk) return;

    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const containerWidth = container.clientWidth;
        const chunkWidth = chunk.scrollWidth;
        const shouldCarousel = chunkWidth > containerWidth + 8;
        setIsCarousel(shouldCarousel);
        setDistance(chunkWidth);
        const durationSeconds = Math.max(18, Math.min(60, chunkWidth / 60));
        setDuration(durationSeconds);
      });
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [logos.length]);

  return (
    <div
      ref={containerRef}
      className={`logo-carousel${isCarousel ? ' is-carousel' : ''}`}
      style={
        distance
          ? ({
              ['--marquee-distance' as any]: `${distance}px`,
              ['--marquee-duration' as any]: `${duration.toFixed(0)}s`,
            } as React.CSSProperties)
          : undefined
      }
      aria-label="Customer logos"
    >
      <div className="logo-carousel__track">
        <div className="logo-carousel__chunk" ref={chunkRef}>
          {logos.map((logo) => (
            <div className="logo-cell" key={logo.name}>
              <LogoCell logo={logo} />
            </div>
          ))}
        </div>
        {isCarousel ? (
          <div className="logo-carousel__chunk" aria-hidden="true">
            {logos.map((logo) => (
              <div className="logo-cell" key={`dup-${logo.name}`}>
                <LogoCell logo={logo} />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Avatar({ src, name }: { src: string; name: string }) {
  const [broken, setBroken] = useState(false);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join('');

  return (
    <div className="team-avatar" aria-hidden="true">
      {!broken ? <img src={src} alt={name} loading="lazy" onError={() => setBroken(true)} /> : <div className="team-avatar__fallback">{initials}</div>}
    </div>
  );
}

type MetricItem = { value: string; label: string; note?: string };

function MetricList({ items }: { items: MetricItem[] }) {
  return (
    <ul className="bridge-metric-list">
      {items.map((item) => (
        <li key={`${item.value}-${item.label}`}>
          <strong>{item.value}</strong>
          <span>{item.label}</span>
          {item.note ? <em>{item.note}</em> : null}
        </li>
      ))}
    </ul>
  );
}

const bridgeNarrative = {
  heroSnapshotMetrics: [
    { value: '$306K', label: 'Bookings in 2025', note: 'vs $228K bookings in 1H 2026' },
    { value: '$225K', label: 'Revenue in 2025', note: 'vs $150K revenue in 1H 2026' },
    { value: '$23K', label: 'MRR in July 2026', note: 'vs $10K MRR in July 2025' },
  ],
  basePlanMetrics: [
    { value: '$600K', label: 'Base sales plan', note: 'Current plan, without added commercial capacity.' },
    { value: '~$47K', label: 'Base MRR reference' },
    { value: '~$560K', label: 'Base ARR run-rate' },
  ],
  basePlanBullets: [
    'The base plan keeps doing what is already working: same product, same market, same value proposition.',
    'It is realistic and grounded in validated demand, active pipeline and customer expansion.',
    'Its limit is execution bandwidth: growth is capped by the current commercial team.',
  ],
  bottlenecks: [
    {
      title: 'Outbound is still underexploited',
      body: 'Quix is not yet generating outbound pipeline with the level of volume and systematization that the market opportunity allows.',
    },
    {
      title: 'Enterprise coverage is too thin',
      body: 'Larger accounts require more senior commercial focus, but current coverage is not deep enough to fully pursue that segment.',
    },
    {
      title: 'The current team is near capacity',
      body: 'Demand, pipeline and use cases are validated. The missing piece is more commercial capacity to capture them faster.',
    },
  ],
  bridgePlanBullets: [
    {
      role: '4 SDRs',
      body: 'Scale outbound pipeline generation and create a more systematic top-of-funnel engine.',
    },
    {
      role: '2 BDRs',
      body: 'Increase senior commercial coverage for larger and more strategic enterprise opportunities.',
    },
    {
      role: '1 AE',
      body: 'Convert the expanded pipeline into revenue without creating a new handoff bottleneck.',
    },
    {
      role: 'Self-service product',
      body: 'Launch a self-serve motion for smaller companies with free trial and auto onboarding.',
    },
  ],
  unchanged: ['Product unchanged', 'Market unchanged', 'Value proposition unchanged'],
  acceleratedMetrics: [
    { value: '~$1.24M', label: 'Modeled sales potential', note: 'Six months after kickoff, with added commercial capacity.' },
    { value: '~$92K', label: 'MRR at month 6' },
    { value: '~$1.10M', label: 'ARR run-rate at month 6' },
  ],
  impactDeltas: [
    { value: '+$643K', label: 'Bookings generated in the first 6 months' },
    { value: '+$45K', label: 'Incremental MRR at month 6' },
    { value: '+$540K', label: 'Incremental ARR run-rate at month 6' },
  ],
  economicsMetrics: [
    { value: '$282K', label: 'Modeled plan cost' },
    { value: '$643K', label: 'Bookings generated in the first 6 months' },
    { value: '2.3x', label: 'Bookings / cost over the 6-month window' },
  ],
  economicsBullets: [
    'Recognized revenue trails bookings because of ramp-up, sales cycle and revenue recognition timing.',
    'The point of the round is not to cover losses. It funds the gap between hiring, ramp-up, closing and collection.',
    'The most important output is the MRR and ARR run-rate Quix builds by the end of the six-month acceleration window.',
  ],
  whyNowBullets: [
    'There is an average ~60-day delay between recognized revenue and cash collection.',
    'The six-month acceleration window starts only once the team and commercial process are in place.',
    'Waiting shifts the entire ramp and delays the higher MRR and ARR run-rate.',
  ],
  raiseRange: 'US$250K-300K',
};

export default function QuixInfo({ exportedByName }: { exportedByName?: string }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const titleBeforePrintRef = useRef<string>('');
  const [printMeta, setPrintMeta] = useState<{ exporter: string; exportedAt: string } | null>(null);
  const printAgenda = useMemo(() => ['Problem', 'How it works', 'Product', 'Traction', 'Base plan', 'Bridge plan', 'Economics', 'Team'], []);
  const sections = useMemo(
    () => [
      { id: 'overview', label: 'Overview' },
      { id: 'problem', label: 'Problem' },
      { id: 'how-it-works', label: 'How It Works' },
      { id: 'product-screens', label: 'Product' },
      { id: 'content-engine', label: 'Content Engine' },
      { id: 'use-cases', label: 'Use Cases' },
      { id: 'traction', label: 'Traction' },
      { id: 'base-plan-2026', label: 'Base Plan' },
      { id: 'commercial-bottleneck', label: 'Bottleneck' },
      { id: 'bridge-plan', label: 'Bridge Plan' },
      { id: 'impact', label: 'Impact' },
      { id: 'economics-why-now', label: 'Economics' },
      { id: 'team', label: 'Team' },
    ],
    []
  );

  const [active, setActive] = useState<string>('overview');

  const revenueGrowth = useMemo(() => {
    return [
      { period: '1H 2025', amount: 90000 },
      { period: '2H 2025', amount: 135000 },
      { period: '1H 2026', amount: 150000 },
    ];
  }, []);

  const signedContracts = useMemo(() => {
    return [
      { period: '1H 2025', amount: 73000 },
      { period: '2H 2025', amount: 233000 },
      { period: '1H 2026', amount: 228000 },
    ];
  }, []);

  useEffect(() => {
    const els = sections
      .map((s) => document.getElementById(s.id))
      .filter(Boolean) as HTMLElement[];
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0));
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      {
        root: null,
        rootMargin: '-20% 0px -70% 0px',
        threshold: [0, 0.1, 0.25, 0.5, 0.75],
      }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [sections]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const topbar = document.querySelector('.topbar') as HTMLElement | null;
    if (!topbar) return;

    let raf = 0;
    const apply = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const h = Math.round(topbar.getBoundingClientRect().height);
        root.style.setProperty('--topbar-h', `${h}px`);
      });
    };

    apply();
    const ro = new ResizeObserver(() => apply());
    ro.observe(topbar);
    window.addEventListener('resize', apply);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', apply);
    };
  }, []);

  useEffect(() => {
    const onBeforePrint = () => {
      document.body.classList.add('is-printing-quix-info');
      titleBeforePrintRef.current = document.title;
      document.title = 'Quix Investor Overview';
      if (printMeta) return;
      const fallbackName = exportedByName?.trim() || 'Quix team';
      setPrintMeta({ exporter: fallbackName, exportedAt: new Date().toISOString() });
    };
    const onAfterPrint = () => {
      document.body.classList.remove('is-printing-quix-info');
      if (titleBeforePrintRef.current) {
        document.title = titleBeforePrintRef.current;
      }
    };

    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [exportedByName, printMeta]);

  const exportPdf = () => {
    const fallbackName = exportedByName?.trim();
    const promptDefault = fallbackName || 'Quix team';
    const prompted = fallbackName || window.prompt('Name to include in the PDF export', promptDefault);
    if (prompted === null) return;
    const exporter = prompted.trim() || promptDefault;

    flushSync(() => {
      setPrintMeta({ exporter, exportedAt: new Date().toISOString() });
    });
    document.body.classList.add('is-printing-quix-info');
    titleBeforePrintRef.current = document.title;
    document.title = 'Quix Investor Overview';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  };

  const printExporter = printMeta?.exporter || exportedByName || 'Quix team';
  const printDate = printMeta
    ? new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        timeZone: 'America/Argentina/Buenos_Aires',
      }).format(new Date(printMeta.exportedAt))
    : '';
  const printTime = printMeta
    ? new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'America/Argentina/Buenos_Aires',
      }).format(new Date(printMeta.exportedAt))
    : '';
  const printTimestamp = [printDate, printTime].filter(Boolean).join(' · ');

  return (
    <div className="qinfo" ref={rootRef}>
      <div className="print-export-meta" aria-hidden="true">
        <span>Exported by {printExporter}</span>
        <span>{printTimestamp}</span>
        <span>Quix Investor Overview</span>
      </div>
      <header className="hero" id="overview" data-print-section="01 Overview">
        <div className="hero__badge">Quix Investor Room</div>
        <div className="hero__brand-lockup" aria-hidden="true">
          <img src="/brand/quix-logo.png" alt="Quix" className="hero__brand-mark" />
          <div className="hero__brand-copy">
            <span>Quix Investor Room</span>
            <strong>Investor overview</strong>
          </div>
        </div>
        <div className="hero__print-meta" aria-hidden="true">
          <span>Investor overview export</span>
          <span>{printExporter}</span>
          <span>{printTimestamp}</span>
        </div>
        <div className="hero__content">
          <div className="hero__copy">
            <h1 className="hero__title">
              <span className="hero__kicker">Investor overview</span>
              Quix delivers custom corporate training via instant messaging
            </h1>
            <p className="hero__lead">
              We transform internal knowledge into interactive, conversational microlearning for frontline teams, delivered through channels such as WhatsApp, web app, and
              Microsoft Teams, with real-time analytics across completion, performance and adoption.
            </p>
            <div className="hero__cta">
              <a className="btn btn--primary" href="#how-it-works">
                How it works
              </a>
              <a className="btn btn--ghost" href="#traction">
                Traction
              </a>
            </div>
          </div>
          <div className="hero__visual">
            <img src="/img/Quix_Logos__Blanco.png" alt="Quix" className="hero__brand" />
            <div className="hero__card bridge-hero-card">
              <h2 className="hero__card-title">Snapshot</h2>
              <MetricList items={bridgeNarrative.heroSnapshotMetrics} />
            </div>
          </div>
        </div>
        <div className="hero__print-agenda" aria-hidden="true">
          {printAgenda.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </header>

      <div className="qinfo-nav-wrap">
        <div className="qinfo-nav-bar">
          <nav className="qinfo-nav" aria-label="Page sections">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={active === s.id ? 'is-active' : ''}
                aria-current={active === s.id ? 'true' : undefined}
                onClick={() => setActive(s.id)}
              >
                {s.label}
              </a>
            ))}
          </nav>
          <button type="button" className="btn qinfo-export-btn" onClick={exportPdf}>
            Export PDF
          </button>
        </div>
      </div>

      <main className="qinfo__main">
        <section className="section" id="problem" data-print-section="02 Problem">
          <div className="section__header">
            <h2>The problem</h2>
            <p>
              In Latin America, over 80% of the workforce is not behind a computer. Traditional training methods do not reach them effectively.
            </p>
          </div>
          <div className="stat-grid" aria-label="Problem key stats">
            <div className="card stat-card">
              <div className="stat-card__num">80%+</div>
              <div className="stat-card__label">LATAM workforce is frontline (not behind a computer)</div>
            </div>
            <div className="card stat-card">
              <div className="stat-card__num">~15%</div>
              <div className="stat-card__label">Average completion on traditional e-learning</div>
            </div>
            <div className="card stat-card">
              <div className="stat-card__num">$5K+</div>
              <div className="stat-card__label">Average cost to produce a course</div>
            </div>
            <div className="card stat-card">
              <div className="stat-card__num">2-3 months</div>
              <div className="stat-card__label">Time to launch a new course</div>
            </div>
          </div>
          <div className="grid grid--two-cols">
            <article className="card">
              <h3>In-person training challenges</h3>
              <ul className="list list--check">
                <li>Distributed teams across multiple locations.</li>
                <li>High trainer and coordination costs.</li>
                <li>Low attendance and inconsistent execution.</li>
                <li>Poor scalability.</li>
              </ul>
            </article>
            <article className="card">
              <h3>"Virtual" training challenges</h3>
              <ul className="list list--check">
                <li>Deskless workers will not install eLearning apps on their phones.</li>
                <li>Multiple-choice validations with limited real-life application.</li>
                <li>No link between content and day-to-day work.</li>
              </ul>
            </article>
          </div>
        </section>

        <section className="section section--highlight section--video" id="how-it-works" aria-label="How Quix works" data-print-section="03 How It Works">
          <div className="section__header">
            <h2>How Quix works</h2>
            <p>From internal knowledge to interactive microlearning delivered via instant messaging, with analytics and continuous improvement.</p>
          </div>
          <div className="how-grid">
            <div className="how-grid__steps" aria-label="How it works steps">
              <div className="how-steps">
                <div className="how-step">
                  <div className="how-step__num" aria-hidden="true">
                    1
                  </div>
                  <div>
                    <div className="how-step__title">Transform knowledge into microlearning</div>
                    <div className="how-step__desc">Turn manuals, SOPs and expert know-how into interactive scripts and capsules.</div>
                  </div>
                </div>
                <div className="how-step">
                  <div className="how-step__num" aria-hidden="true">
                    2
                  </div>
                  <div>
                    <div className="how-step__title">Deliver via WhatsApp</div>
                    <div className="how-step__desc">Training reaches frontline teams where they already are, with reminders and evaluations.</div>
                  </div>
                </div>
                <div className="how-step">
                  <div className="how-step__num" aria-hidden="true">
                    3
                  </div>
                  <div>
                    <div className="how-step__title">Measure and improve</div>
                    <div className="how-step__desc">Real-time analytics across completion and performance, plus an assistant to explore insights.</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="how-grid__video">
              <div className="video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/huARncFy-20?rel=0"
                  title="Video: How Quix works"
                  loading="lazy"
                  frameBorder={0}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>

        <section className="section" id="product-screens" aria-label="Product overview" data-print-section="04 Product">
          <div className="section__header">
            <h2>Product</h2>
            <p>Quix turns existing company knowledge into practical, mobile-first training journeys delivered through WhatsApp.</p>
          </div>
          <div className="section--asset" aria-label="Quix product overview">
            <img src="/img/slidequix.png" alt="Quix product overview" loading="lazy" />
          </div>
        </section>

        <section className="section" id="content-engine" data-print-section="05 Content Engine">
          <div className="content-creation">
            <div className="content-creation__copy">
              <div className="section__header">
                <h2>From knowledge to runnable programs</h2>
                <p>
                  We start from existing materials and convert them into conversational microlearning modules ready to deliver,
                  assess, and measure.
                </p>
              </div>
              <ul className="list list--check content-creation__list">
                <li>Synthesizes and structures key knowledge into microlearning scripts.</li>
                <li>Combines formats (text, audio, video) while keeping editorial control.</li>
                <li>Delivers content ready to automate and measure in learning programs.</li>
              </ul>
            </div>
            <div className="content-creation__image">
              <img
                src="/img/creamos-el-contenido.png"
                alt="Diagram: Quix transforms manuals and PDFs into microlearning modules"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="section section--muted section--print-page" id="use-cases" data-print-section="06 Use Cases">
          <div className="section__header">
            <h2>Where it works</h2>
            <p>
              Quix meets teams in the channels they already use. For frontline operations that often means WhatsApp, while other use cases can be served through web and
              enterprise collaboration environments without changing the learning methodology.
            </p>
          </div>
          <div className="usecases-grid">
            <article className="card usecases-card">
              <span className="usecases-card__tag">Industries</span>
              <h3>Retail</h3>
              <p>Update frontline teams in real time with practical mobile training for operations and customer experience.</p>
            </article>
            <article className="card usecases-card">
              <span className="usecases-card__tag">Industries</span>
              <h3>Health and Insurance</h3>
              <p>Keep distributed teams aligned on protocols, compliance, and service quality through continuous microlearning.</p>
            </article>
            <article className="card usecases-card">
              <span className="usecases-card__tag">Industries</span>
              <h3>Manufacturing</h3>
              <p>Train deskless teams on critical procedures to reduce operational errors and improve execution consistency.</p>
            </article>
            <article className="card usecases-card">
              <span className="usecases-card__tag">Use Cases</span>
              <h3>Sales enablement</h3>
              <p>Improve win rates with always-on training delivered where commercial teams already work.</p>
            </article>
            <article className="card usecases-card">
              <span className="usecases-card__tag">Use Cases</span>
              <h3>Process Training</h3>
              <p>Standardize key workflows with short, repeatable learning paths that reinforce correct execution.</p>
            </article>
            <article className="card usecases-card">
              <span className="usecases-card__tag">Use Cases</span>
              <h3>Onboarding</h3>
              <p>Accelerate ramp-up with structured role-based journeys that reduce time to productivity.</p>
            </article>
          </div>
        </section>

        <section className="section section--print-page" id="traction" data-print-section="07 Traction">
          <div className="section__header">
            <h2>Traction</h2>
            <p>
              Fast growth since MVP launch (all figures shown in USD as per the deck).
            </p>
          </div>

          <div className="traction-layout">
            <div className="traction-kpis">
              <div className="kpi-grid">
                <article className="card kpi-card">
                  <h3>$ Contracts Signed 2025</h3>
                  <div className="kpi-card__body">
                    <div className="metric">$306K</div>
                  </div>
                </article>
                <article className="card kpi-card">
                  <h3>Revenue (2025)</h3>
                  <div className="kpi-card__body">
                    <div className="metric">$225K</div>
                  </div>
                </article>
                <article className="card kpi-card">
                  <h3>MRR (July 2026)</h3>
                  <div className="kpi-card__body">
                    <div className="metric">$23K</div>
                    <div className="sub">Monthly recurring revenue</div>
                  </div>
                </article>
                <article className="card kpi-card">
                  <h3>Churn</h3>
                  <div className="kpi-card__body kpi-card__body--split">
                    <div className="kpi-card__copy">
                      <div className="metric">&lt;5%</div>
                    </div>
                    <Donut percent={0} color="rgba(27,54,93,0.85)" />
                  </div>
                </article>
                <article className="card kpi-card">
                  <h3>Upsell</h3>
                  <div className="kpi-card__body kpi-card__body--split">
                    <div className="kpi-card__copy">
                      <div className="metric">70%</div>
                      <div className="sub">Within the first 12 months</div>
                    </div>
                    <Donut percent={70} color="rgba(0,231,228,0.95)" />
                  </div>
                </article>
                <article className="card kpi-card">
                  <h3>Users trained</h3>
                  <div className="kpi-card__body">
                    <div className="metric">35,000+</div>
                  </div>
                </article>
              </div>
            </div>

            <div className="card traction-milestones">
              <div className="milestones-header">Milestones</div>
              <div className="timeline" role="list">
                <div className="timeline__item" role="listitem">
                  <div className="timeline__dot" aria-hidden="true" />
                  <div className="timeline__content">
                    <div className="timeline__when">May 2024</div>
                    <div className="timeline__title">Quix MVP launched</div>
                  </div>
                </div>
                <div className="timeline__item" role="listitem">
                  <div className="timeline__dot" aria-hidden="true" />
                  <div className="timeline__content">
                    <div className="timeline__when">2H 2024</div>
                    <div className="timeline__title">
                      <b>$137K</b> bookings · <b>$45K</b> revenue · Pre-seed round <b>$400K</b>
                    </div>
                  </div>
                </div>
                <div className="timeline__item" role="listitem">
                  <div className="timeline__dot" aria-hidden="true" />
                  <div className="timeline__content">
                    <div className="timeline__when">1H 2025</div>
                    <div className="timeline__title">
                      <b>$73K</b> bookings · <b>$90K</b> revenue · <b>13</b> customers
                    </div>
                  </div>
                </div>
                <div className="timeline__item" role="listitem">
                  <div className="timeline__dot" aria-hidden="true" />
                  <div className="timeline__content">
                    <div className="timeline__when">2H 2025</div>
                    <div className="timeline__title">
                      <b>$233K</b> bookings · <b>$135K</b> revenue · <b>20</b> customers
                    </div>
                    <div className="timeline__note">Launch Peru operations</div>
                  </div>
                </div>
                <div className="timeline__item" role="listitem">
                  <div className="timeline__dot" aria-hidden="true" />
                  <div className="timeline__content">
                    <div className="timeline__when">1H 2026</div>
                    <div className="timeline__title">
                      <b>$228K</b> bookings · <b>$150K</b> revenue
                    </div>
                    <div className="timeline__note"><b>$279K</b> committed revenue</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="traction-charts">
              <SemesterBarChart
                title="Revenue growth"
                subtitle="Revenue by semester, including 1H 2026."
                metricLabel="Revenue"
                items={revenueGrowth}
                tone="revenue"
              />
              <SemesterBarChart
                title="Signed contracts"
                subtitle="Signed contracts by semester, including 1H 2026."
                metricLabel="Signed contracts"
                items={signedContracts}
                tone="contracts"
              />
            </div>

            <div className="logo-section traction-customers" aria-label="Customers">
              <div className="logo-section__title">Leading brands already train their teams with Quix</div>
              {customerLogos.length ? (
                <LogoCarousel logos={customerLogos} />
              ) : (
                <div className="muted" style={{ fontSize: 12 }}>
                  No logos found. Add files to <code>public/img/logos</code> and restart.
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="section section--muted" id="base-plan-2026" data-print-section="08 Base Plan">
          <div className="section__header">
            <h2>What the base plan already delivers</h2>
            <p>
              Even without new capital, Quix has a credible path to a $600K sales plan. The bridge case is not about fixing weakness; it is about accelerating a plan that
              already works on its own.
            </p>
          </div>

          <div className="bridge-comparison-grid">
            <article className="card bridge-plan-card">
              <div className="bridge-plan-card__eyebrow">Without new capital</div>
              <h3>Base plan</h3>
              <MetricList items={bridgeNarrative.basePlanMetrics} />
            </article>

            <article className="card bridge-plan-card">
              <div className="bridge-plan-card__eyebrow">What it means</div>
              <h3>Solid, but capacity-constrained</h3>
              <ul className="bridge-bullet-list">
                {bridgeNarrative.basePlanBullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          </div>
        </section>

        <section className="section section--print-page" id="commercial-bottleneck" data-print-section="09 Bottleneck">
          <div className="section__header">
            <h2>The bottleneck is commercial capacity</h2>
            <p>
              Demand, pipeline and use cases are already validated. The constraint is not product or market. The constraint is how much commercial bandwidth Quix has to turn
              that validated demand into revenue quickly enough.
            </p>
          </div>

          <div className="round-grid">
            {bridgeNarrative.bottlenecks.map((item, index) => (
              <article className="card round-card" key={item.title}>
                <div className="round-card__tag">{String(index + 1).padStart(2, '0')}</div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section section--highlight" id="bridge-plan" data-print-section="10 Bridge Plan">
          <div className="section__header">
            <h2>The Bridge Plan</h2>
            <p>
              The Bridge Round is a direct investment in commercial capacity over the first six months after kickoff. It adds pipeline generation, senior enterprise coverage
              and closing bandwidth without changing the product, the market or the value proposition.
            </p>
          </div>

          <div className="round-grid">
            {bridgeNarrative.bridgePlanBullets.map((item, index) => (
              <article className="card bridge-plan-card" key={item.role}>
                <div className="bridge-plan-card__eyebrow">Hiring line {index + 1}</div>
                <h3>{item.role}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>

          <ul className="hero__tags bridge-unchanged" aria-label="What does not change">
            {bridgeNarrative.unchanged.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="section" id="impact" data-print-section="11 Impact">
          <div className="section__header">
            <h2>What changes with the round</h2>
            <p>
              The modeled impact is measured over the first six months after kickoff, not against a fixed calendar year. The main value is the stronger MRR and ARR run-rate
              Quix builds by the end of that window.
            </p>
          </div>

          <div className="bridge-comparison-grid">
            <article className="card bridge-plan-card">
              <div className="bridge-plan-card__eyebrow">Scenario A</div>
              <h3>Base plan</h3>
              <MetricList items={bridgeNarrative.basePlanMetrics} />
            </article>

            <article className="card bridge-plan-card bridge-plan-card--accent">
              <div className="bridge-plan-card__eyebrow">Scenario B</div>
              <h3>Six months after kickoff</h3>
              <MetricList items={bridgeNarrative.acceleratedMetrics} />
            </article>
          </div>

          <div className="bridge-delta-grid">
            {bridgeNarrative.impactDeltas.map((item) => (
              <article className="card stat-card bridge-delta-card" key={item.label}>
                <div className="stat-card__num">{item.value}</div>
                <div className="stat-card__label">{item.label}</div>
              </article>
            ))}
          </div>
        </section>

        <section className="section section--muted" id="economics-why-now" data-print-section="12 Economics">
          <div className="section__header">
            <h2>Economics and why now</h2>
            <p>
              This round funds the six-month execution window after kickoff. It bridges the gap between hiring, ramp-up, closing and collection so Quix can build a materially
              stronger commercial engine and recurring-revenue run-rate.
            </p>
          </div>

          <div className="bridge-comparison-grid">
            <article className="card bridge-plan-card">
              <div className="bridge-plan-card__eyebrow">Economics</div>
              <h3>Commercial acceleration math</h3>
              <MetricList items={bridgeNarrative.economicsMetrics} />
              <div style={{ height: 16 }} />
              <ul className="bridge-bullet-list">
                {bridgeNarrative.economicsBullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>

            <article className="card bridge-plan-card bridge-plan-card--accent">
              <div className="bridge-plan-card__eyebrow">Why now</div>
              <h3>A slower path is a smaller plan</h3>
              <ul className="bridge-bullet-list">
                {bridgeNarrative.whyNowBullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="bridge-raise-banner">
                <div className="bridge-plan-card__eyebrow">Target raise</div>
                <div className="metric">{bridgeNarrative.raiseRange}</div>
                <div className="sub">Presented as the Bridge Round range, with US$282K as the modeled plan cost.</div>
              </div>
            </article>
          </div>
        </section>

        <section className="section" id="team" data-print-section="13 Team">
          <div className="section__header">
            <h2>Team</h2>
            <p>Leadership across edtech, fundraising, finance and AI, with the operating experience required to execute the next commercial step.</p>
          </div>
          <div className="team-grid">
            <article className="card team-card">
              <div className="team-card__top">
                <Avatar src="/img/team/ignacio-barrea.jpg" name="Ignacio Barrea" />
                <div>
                  <div className="team-card__name">Ignacio Barrea</div>
                  <div className="team-card__role">CEO, Co-Founder</div>
                </div>
              </div>
              <p>10+ years C-Level in edtech. MBA, PhD candidate, former COO and VP in startups that raised more than $80M.</p>
            </article>
            <article className="card team-card">
              <div className="team-card__top">
                <Avatar src="/img/team/santiago-gomez.jpg" name="Santiago Gomez" />
                <div>
                  <div className="team-card__name">Santiago Gomez</div>
                  <div className="team-card__role">CRO, Co-Founder</div>
                </div>
              </div>
              <p>Commercial operator with 10+ years of experience across partnerships, go-to-market and business development in education and growth environments.</p>
            </article>
            <article className="card team-card">
              <div className="team-card__top">
                <Avatar src="/img/team/julia-insua.jpg" name="Julia Insua" />
                <div>
                  <div className="team-card__name">Julia Insua</div>
                  <div className="team-card__role">CFO, Co-Founder</div>
                </div>
              </div>
              <p>MBA with 15+ years in finance leadership, including due diligence and M&A processes with Kaszek, Riverwood, Globant and MercadoLibre.</p>
            </article>
            <article className="card team-card">
              <div className="team-card__top">
                <Avatar src="/img/team/carlos-alarcon.jpg" name="Carlos Alarcon" />
                <div>
                  <div className="team-card__name">Carlos Alarcon</div>
                  <div className="team-card__role">CTO, Co-Founder</div>
                </div>
              </div>
              <p>10+ years as tech lead. Google Developer Expert and Microsoft MVP in AI, with deep experience shipping applied AI products.</p>
            </article>
          </div>
        </section>
      </main>
    </div>
  );
}
